import { AxiosResponse } from 'axios';
import qs from 'qs';
import { axiosApiInstance } from 'Store';
import {
    MessagesAdvertType,
    MessageSenders,
    UserOffersResponseContent,
    UserProfileOffersResponseContent
} from 'store/User/State';
import { convertPrice } from 'utils/convertPrice';
import { getPropertyByTypeUser } from 'utils/formatters/getPropertyByType';
import { stringToNumber } from 'utils/formatters/stringToNumber';
import { UploadRequestData } from 'utils/types/File';
import { UserSavedFilter } from 'utils/types/Filter';
import { OfferData, UserReactions } from 'utils/types/OfferData';
import {
    UserAddAdvertStateType,
    UserFilterReturnData,
    UserOfferRequestData,
    V2OfferType
} from 'utils/types/UsersAdvert';
import { CheckOpinionParamsOpinionTypeEnum, MyDataDto, UserResourcesDto } from 'utils/types/UserSettingsModels';
import { LocationItem } from 'utils/types/V2Locations';
import { v4 } from 'uuid';

import { activeNotification } from 'components/functions/activeNotification';

export interface LogExceptionContext {
    request?: {
        url?: string;
        method?: string;
        body?: string;
        params?: string;
    };
    response?: {
        status?: number;
        body?: string;
    };
    user?: object;
    appVersion?: string;
    userAgent?: string;
    timestamp?: string;
    location?: string;
    functionName?: string;
    action?: string;
}

const authenticate = async (email: string, password: string) => {
    return axiosApiInstance.post('/auth/authenticate', { username: email, password })
        .then((resp) => {
            return {
                user: resp.data.profile,
                token: resp.data.accessToken,
                refreshToken: resp.data.refreshToken,
                roles: resp.data.roles,
                organization: resp.data.organization,
                organizationRole: resp.data.organizationRole,
                isWordpressUser: resp.data.ID !== null && resp.data.ID !== undefined
            };
        })
        .catch(({ response }) => {
            switch (response.status) {
            case 403:
                switch (response.data?.code) {
                case '[jwt_auth] incorrect_password':
                    activeNotification(
                        'Błędne hasło',
                        'Wpisz poprawne hasło',
                        'danger'
                    );
                    break;
                default:
                    activeNotification(
                        'Coś poszło nie tak!',
                        response.data.Message,
                        'danger',
                        5000
                    );
                    break;
                }
                break;
            case 401:
                activeNotification(
                    'Logowanie',
                    response.data.Message,
                    'danger'
                );
                break;
            case 404:
                activeNotification(
                    'Błedne dane logowania',
                    response.data.Message,
                    'danger'
                );
                break;
            case 423:
                activeNotification(
                    'Przekroczona liczba zapytań',
                    'Konto zostało tymczasowo zablokowane na 15 minut. Spróbuj ponownie po tym czasie.',
                    'danger'
                );
                break;
            case 500:
                activeNotification(
                    'Błąd serwera',
                    response.data,
                    'danger'
                );
                break;
            default:
                activeNotification(
                    'Coś poszło nie tak!',
                    'Spróbuj ponownie',
                    'danger'
                );
                break;
            }

            return false;
        });
};

const authenticateByToken = async (refreshToken: string) => {
    axiosApiInstance.defaults.headers.common = { Authorization: false };

    return axiosApiInstance.put('/auth/refreshToken', {
        refreshToken
    })
        .then((resp) => {
            if (resp) {
                return {
                    user: resp.data.profile,
                    token: resp.data.accessToken,
                    refreshToken: resp.data.refreshToken,
                    roles: resp.data.roles,
                    organization: resp.data.organization,
                    organizationRole: resp.data.organizationRole,
                    isWordpressUser: resp.data.ID !== null && resp.data.ID !== undefined

                };
            }
        })
        .catch((resp) => {
            if (resp.response.status === 403) {
                localStorage.removeItem('isRefreshingToken');

                return false;
            } else if (resp.response.status === 500) {
                const errorId = v4();
                userApiRequests.logException(resp, errorId, {
                    action: 'refreshToken',
                    functionName: 'authenticateByToken',
                    request: {
                        url: resp.config.url,
                        method: resp.config.method,
                        body: resp.config.data,
                        params: resp.config.params
                    },
                    response: {
                        status: resp.response.status,
                        body: resp.response.data
                    }
                });

                activeNotification(
                    'Błąd serwera',
                    resp.data.message,
                    'danger'
                );

                return false;
            } else {
                activeNotification(
                    'Coś poszło nie tak!',
                    'Nie udało się odświeżyć sesji, zaloguj się ponownie',
                    'danger'
                );
            }

            return false;
        });
};

const logout = async () => {
    return axiosApiInstance.get('/auth/logout')
        .then(() => {
            activeNotification(
                'Zrobione!',
                'Pomyślnie wylogowano',
                'success'
            );

            return true;
        })
        .catch((resp) => {
            if (resp.response.status === 500) {
                activeNotification(
                    'Błąd serwera',
                    resp.data.message,
                    'danger'
                );
            }

            return false;
        });
};

const getSavedFilters = async (): Promise<UserSavedFilter[]> => {
    return axiosApiInstance
        .get<UserSavedFilter[]>('/v2/user-offer/filter/list')
        .then((resp) => {
            if (resp.data) {
                return resp.data.map((el) => ({
                    ...el,
                    name: el.name
                }));
            }

            return [];
        })
        .catch((err) => {
            if (err?.response?.status === 401) {
                window.parent.location.reload();
            }

            return [];
        });
};

const getFilter = (filterId: number, filterOfferType: V2OfferType): Promise<UserFilterReturnData | void> => {
    return axiosApiInstance
        .get<UserFilterReturnData>(`/v2/user-offer/filter/detail/${filterOfferType}?filterId=${filterId}`)
        .then((resp) => {
            const filters = resp.data;
            // Backend zwraca oba pola: locations (stare ID, dla kompatybilności starej apki mobilnej) i filterLocations (nowe OSM ID).
            // Dla nowej apki czytamy filterLocations.
            const savedLocations = filters.filterLocations && Array.isArray(filters.filterLocations)
                ? Object.fromEntries(
                    filters.filterLocations.map((location: LocationItem) => [
                        location.id,
                        // @ts-expect-error z Api dostajemy fullName
                        { ...location, label: location.fullName }
                    ])
                )
                : {};

            return {
                ...filters,
                offerType: filterOfferType,
                savedLocations
            };
        })
        .catch(() => {
            activeNotification('Problem z załadowaniem danych', 'Niestety nie udało się załadować danych. Spróbuj ponownie później', 'danger');
        });
};

const updateFilter = (params: UserOfferRequestData, propertyType: V2OfferType): Promise<AxiosResponse> => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { locations, ...paramsWithoutLocations } = params;
    const locationIds = Object.keys(params.savedLocations).map((id) => {
        return { id: Number(id) };
    });

    return axiosApiInstance({
        method: 'put',
        url: `/v2/user-offer/filter/edit/${propertyType}`,
        // @ts-expect-error fix in next phase
        params: { id: params.id },
        data: {
            ...paramsWithoutLocations,
            filterLocations: locationIds
        },
        paramsSerializer: function (params) {
            return qs.stringify(params, { arrayFormat: 'repeat' });
        }
    })
        .then((resp) => {
            activeNotification('Udało się!', 'Filtr został zaktualizowany', 'success');

            return resp;
        })
        .catch((err) => {
            activeNotification('Problem z zapisem', 'Niestety nie udało się zapisać danych. Spróbuj ponownie później', 'danger');

            return Promise.reject(err);
        });
};

const deleteFilter = (filterId: number, adType: V2OfferType) => {
    return axiosApiInstance
        .delete(`/v2/user-offer/filter/delete?filterId=${filterId}&module=${getPropertyByTypeUser(adType)}`)
        .then(() => {
            activeNotification('Udało się!', 'Filtr został usunięty', 'success');
        })
        .catch(() => {
            activeNotification('Problem z usunięciem', 'Niestety nie udało się usunąć danych. Spróbuj ponownie później', 'danger');
        });
};

const addFilter = (params: UserOfferRequestData, propertyType: V2OfferType): Promise<AxiosResponse<number>> => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { locations, ...paramsWithoutLocations } = params;
    const locationIds = Object.keys(params.savedLocations).map((id) => {
        return { id: Number(id) };
    });

    return axiosApiInstance({
        method: 'post',
        url: `/v2/user-offer/filter/add/${propertyType}`,
        data: {
            ...paramsWithoutLocations,
            filterLocations: locationIds
        },
        paramsSerializer: function (params) {
            return qs.stringify(params);
        }
    })
        .then((resp) => {
            activeNotification('Udało się!', 'Filtr został dodany', 'success');

            return resp;
        })
        .catch((err) => {
            if (err?.response?.status === 400) {
                activeNotification(
                    'Błąd',
                    err.response.data,
                    'danger'
                );

                return Promise.reject(err);
            } else {
                activeNotification(
                    'Coś poszło nie tak!',
                    'Spróbuj ponownie',
                    'danger'
                );

                return Promise.reject(err);
            }
        });
};

const addImage = (params: UploadRequestData, offerType: V2OfferType): Promise<AxiosResponse<string>> => {
    const formData = new FormData();
    formData.append('file', params.file);

    return axiosApiInstance.post(`/v2/user-offer/photo/advertisement/add?module=${getPropertyByTypeUser(offerType)}`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    })
        .then((resp) => {
            return resp.data;
        })
        .catch((err) => {
            if (err?.response?.status === 400) {
                activeNotification(
                    'Błąd',
                    err.response.data,
                    'danger'
                );

                return Promise.reject(err);
            } else {
                activeNotification(
                    'Coś poszło nie tak!',
                    'Spróbuj ponownie',
                    'danger'
                );

                return Promise.reject(err);
            }
        });
};

const addFile = (params: UploadRequestData, offerType: V2OfferType): Promise<AxiosResponse<string>> => {
    const formData = new FormData();
    formData.append('file', params.file);

    return axiosApiInstance.post(`/v2/user-offer/document/add?module=${getPropertyByTypeUser(offerType)}`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    })
        .then((resp) => {
            return resp.data;
        })
        .catch((err) => {
            if (err?.response?.status === 400) {
                activeNotification(
                    'Błąd',
                    err.response.data,
                    'danger'
                );

                return Promise.reject(err);
            } else {
                activeNotification(
                    'Coś poszło nie tak!',
                    'Spróbuj ponownie',
                    'danger'
                );

                return Promise.reject(err);
            }
        });
};

const downloadFile = (documentId: string, offerType: V2OfferType) => {
    return axiosApiInstance.get('/v2/user-offer/document/download', {
        responseType: 'blob',
        params: {
            fileName: documentId,
            module: getPropertyByTypeUser(offerType)
        }
    })
        .then((resp) => {
            const url = URL.createObjectURL(new Blob([resp.data]));
            const link = document.createElement('a');

            link.setAttribute('href', url);
            link.setAttribute('download', documentId);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        })
        .catch((err) => {
            if (err?.response?.status === 400) {
                activeNotification(
                    'Błąd',
                    err.response.data,
                    'danger'
                );

                return Promise.reject(err);
            } else {
                activeNotification(
                    'Coś poszło nie tak!',
                    'Spróbuj ponownie',
                    'danger'
                );

                return Promise.reject(err);
            }
        });
};

const getOffers = (page: number, size: number, params?: UserOfferRequestData, offerType?: V2OfferType): Promise<AxiosResponse> => {
    let processedParams = params;

    if (params) {
        const { locations, ...paramsWithoutLocations } = params;
        Object.keys(params).forEach((key) => {
            if ((Array.isArray(params[key as keyof typeof params]) || typeof params[key as keyof typeof params] === 'string') && (params[key as keyof typeof params] as []).length === 0) {
                delete params[key as keyof typeof params];
            }
        });
        const locationIds = params.savedLocations
            ? Object.keys(params.savedLocations).map((id) => {
                return { id: Number(id) };
            })
            : [];

        // jeśli użytkownik nie wybierze żadnej lokalizacji, wysyłamy do Api wszystkie lokalizacje.
        const allLocations = locations
            ? locations.map((el) => {
                return { id: el.id };
            })
            : [];

        processedParams = {
            ...paramsWithoutLocations,
            // @ts-expect-error podczas wysyłania do Api label nie jest potrzebny
            filterLocations: locationIds.length > 0 ? locationIds : allLocations
        };
    }

    return axiosApiInstance({
        method: offerType ? 'post' : 'get',
        url: `/v2/user-offer/advertisement/search/${offerType || 'all'}`,
        params: {
            page,
            size
        },
        data: {
            ...processedParams,
            ...processedParams?.daysAmount === -1 && { dateAddedFrom: new Date(1970) }
            // priceFrom: stringToNumber(params.priceFrom),
            // priceTo: stringToNumber(params.priceTo),
            // areaFrom: stringToNumber(params.areaFrom),
            // areaTo: stringToNumber(params.areaTo),
            // pricePerSquareMeterFrom: stringToNumber(params.pricePerSquareMeterFrom),
            // pricePerSquareMeterTo: stringToNumber(params.pricePerSquareMeterTo),
            // rentPriceFrom: stringToNumber(params.rentPriceFrom),
            // rentPriceTo: stringToNumber(params.rentPriceTo),
            // depositPriceFrom: stringToNumber(params.depositPriceFrom),
            // depositPriceTo: stringToNumber(params.depositPriceTo),
        }
    })
        .then((resp) => {
            if (resp.status === 204) {
                return { empty: true };
            }

            if (resp.data) {
                return {
                    ...resp.data,
                    content: resp.data.content.map((el: UserOffersResponseContent) => ({
                        ...el,
                        price: el.price ? convertPrice(el.price) : '',
                        maximumInvestmentValue: el.maximumInvestmentValue ? convertPrice(el.maximumInvestmentValue) : '',
                        area: el.area ? convertPrice(el.area) : '',
                        investorContribution: el.investorContribution ? convertPrice(el.investorContribution) : ''
                    }))
                };
            }
        })
        .catch(() => {
            activeNotification(
                'Nie znaleziono wyników',
                'Wyszukiwanie nie powiodło się, spróbuj jeszcze raz.',
                'warning'
            );

            return [{ empty: true }];
        });
};

const getAllOffers = (page: number, size: number): Promise<AxiosResponse> => {
    return axiosApiInstance({
        method: 'post',
        url: '/v2/user-offer/advertisement/search/all',
        params: {
            page,
            size
        }
    })
        .then((resp) => {
            if (resp.status === 204) {
                return { empty: true };
            }

            if (resp.data) {
                return {
                    ...resp.data,
                    content: resp.data.content.map((el: UserProfileOffersResponseContent) => ({
                        ...el,
                        price: el.price ? convertPrice(el.price) : '',
                        maximumInvestmentValue: el.maximumInvestmentValue ? convertPrice(el.maximumInvestmentValue) : '',
                        area: el.area ? convertPrice(el.area) : '',
                        investorContribution: el.investorContribution ? convertPrice(el.investorContribution) : ''
                    }))
                };
            }
        })
        .catch(() => {
            activeNotification(
                'Nie znaleziono wyników',
                'Wyszukiwanie nie powiodło się, spróbuj jeszcze raz.',
                'warning'
            );

            return [{ empty: true }];
        });
};

const getOfferDetails = (offerType: V2OfferType, id: string): Promise<AxiosResponse> => {
    return axiosApiInstance
        .get(`/v2/user-offer/advertisement/detail/${offerType}?advertisementId=${id}`)
        .then((resp) => {
            if (resp.data) {
                return {
                    ...resp.data,
                    price: resp.data.price ? convertPrice(resp.data.price) : ''
                };
            }
        })
        .catch(() => {
            activeNotification(
                'Nie udało się pobrać ogłoszenia',
                'Nie udało się pobrać danych szczegółowych ogłoszenia',
                'warning'
            );

            return [{ empty: true }];
        });
};

const getMyOffers = (offerType: V2OfferType, page: number, size: number): Promise<AxiosResponse> => {
    return axiosApiInstance({
        method: 'get',
        url: '/v2/user-offer/advertisement/edit/list',
        params: {
            page,
            module: getPropertyByTypeUser(offerType),
            size
        }
    })
        .then((resp) => {
            if (resp.status === 204) {
                return { empty: true };
            }

            if (resp.data) {
                return {
                    ...resp.data,
                    content: resp.data.content.map((el: UserOffersResponseContent) => ({
                        ...el,
                        price: el.price ? convertPrice(el.price) : '',
                        area: el.area ? convertPrice(el.area) : '',
                        maximumInvestmentValue: el.maximumInvestmentValue ? convertPrice(el.maximumInvestmentValue) : '',
                        investorContribution: el.investorContribution ? convertPrice(el.investorContribution) : ''
                    }))
                };
            }
        })
        .catch(() => {
            activeNotification(
                'Nie znaleziono wyników',
                'Wyszukiwanie nie powiodło się, spróbuj jeszcze raz.',
                'warning'
            );

            return [{ empty: true }];
        });
};

const getMyOfferDetails = (offerType: V2OfferType, advertisementId: number | string): Promise<AxiosResponse> => {
    return axiosApiInstance({
        method: 'get',
        url: `/v2/user-offer/advertisement/edit/${offerType}/detail`,
        params: {
            advertisementId
        }
    })
        .then((resp) => {
            if (resp.data) {
                return {
                    ...resp.data,
                    // Backend zwraca filterLocation (nowe OSM ID) i locations (stare ID dla kompatybilności mobilnej).
                    // Czytamy filterLocation żeby wysłać poprawne ID przy edycji.
                    city: resp.data.filterLocation
                };
            }
        })
        .catch(() => {
            activeNotification(
                'Nie znaleziono wyników',
                'Wyszukiwanie nie powiodło się, spróbuj jeszcze raz.',
                'warning'
            );

            return [{ empty: true }];
        });
};

const deleteMyOffer = (advertisementId: number | string, offerType: V2OfferType): Promise<AxiosResponse> => {
    return axiosApiInstance({
        method: 'delete',
        url: '/v2/user-offer/advertisement/edit/delete',
        params: {
            module: getPropertyByTypeUser(offerType),
            advertisementId
        }
    })
        .then((resp) => {
            if (resp.data) {
                activeNotification(
                    'Udało się!',
                    'Ogłoszenie zostało usunięte',
                    'success'
                );

                return resp.data;
            }
        })
        .catch(() => {
            activeNotification(
                'Coś poszło nie tak!',
                'Nie udało się usunąć ogłoszenia',
                'warning'
            );
        });
};

const addNewOffer = (params: UserAddAdvertStateType): Promise<AxiosResponse> => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { locations, ...paramsWithoutLocations } = params;
    const locationIds = Object.keys(params.savedLocations).map((id) => {
        return { id: Number(id) };
    });

    const requestParams = {
        ...paramsWithoutLocations,
        filterLocations: locationIds,
        price: stringToNumber(params.price),
        area: stringToNumber(params.area),
        expectedProfit: stringToNumber(params.expectedProfit),
        renovationExpenses: stringToNumber(params.renovationExpenses),
        investmentBudget: stringToNumber(params.investmentBudget),
        roi: stringToNumber(params.roi),
        leaseFee: stringToNumber(params.leaseFee),
        ownContribution: stringToNumber(params.ownContribution),
        investorContribution: stringToNumber(params.investorContribution),
        totalInvestmentValue: stringToNumber(params.totalInvestmentValue),
        minimumSalePrice: stringToNumber(params.minimumSalePrice),
        maximumSalePrice: stringToNumber(params.maximumSalePrice),
        formOfProfit: params.formOfProfit,
        minimumProfitAmount: stringToNumber(params.minimumProfitAmount),
        expectedReturnRate: stringToNumber(params.expectedReturnRate),
        floor: stringToNumber(params.floor),
        floors: stringToNumber(params.floors),
        rentalIncome: stringToNumber(params.rentalIncome),
        propertyCondition: params.propertyCondition,
        percentageOfInvestment: stringToNumber(params.percentageOfInvestment),
        investmentAmountFrom: stringToNumber(params.investmentAmountFrom),
        investmentAmountTo: stringToNumber(params.investmentAmountTo),
        minimumInvestmentValue: stringToNumber(params.minimumInvestmentValue),
        maximumInvestmentValue: stringToNumber(params.maximumInvestmentValue),
        investmentTimeFrom: stringToNumber(params.investmentTimeFrom),
        investmentTimeTo: stringToNumber(params.investmentTimeTo),
        availableFrom: params.availableFrom ? new Date(params.availableFrom) : undefined,
        estimatedSaleDate: params.estimatedSaleDate ? new Date(params.estimatedSaleDate) : undefined
    };
    const json = JSON.stringify({ ...requestParams });
    const blob = new Blob([json], {
        type: 'application/json'
    });
    const formData = new FormData();
    formData.append('addDto', blob);

    return axiosApiInstance({
        headers: { 'Content-Type': 'application/json' },
        method: 'post',
        url: `/v2/user-offer/advertisement/add/${params.offerType}`,
        data: requestParams
    })
        .then((resp) => {
            return resp;
        })
        .catch((err) => {
            if (err?.response?.status === 400) {
                activeNotification(
                    'Błąd',
                    err.response.data,
                    'danger'
                );

                return Promise.reject(err);
            } else {
                activeNotification(
                    'Coś poszło nie tak!',
                    'Spróbuj ponownie',
                    'danger'
                );

                return Promise.reject(err);
            }
        });
};

const setOfferReaction = async (id: string, city: string, reactions: UserReactions, offerType: V2OfferType) => {
    return axiosApiInstance({
        method: 'PUT',
        url: 'reaction/process-uuid',
        params: {
            module: getPropertyByTypeUser(offerType)
        },
        data: {
            ownerId: id,
            ...reactions,
            city
        }
    })
        .then((resp) => {
            return resp.data;
        })
        .catch(() => {
            activeNotification(
                'Coś poszło nie tak!',
                'Spróbuj ponownie',
                'danger'
            );

            return false;
        });
};

const getNotes = (offerType: V2OfferType, id: string) => {
    return axiosApiInstance({
        url: 'note/get-notes-uuid',
        method: 'get',
        params: {
            module: getPropertyByTypeUser(offerType),
            ownerId: id
        }
    })
        .then((resp) => {
            if (resp.data && resp.data.length > 0) {
                return resp.data;
            }

            return [];
        })
        .catch(() => {
            return [];
        });
};

const deleteNote = (offerType: V2OfferType, noteId: string) => {
    return axiosApiInstance({
        method: 'delete',
        url: `note/delete-uuid/${noteId}`,
        params: {
            module: getPropertyByTypeUser(offerType)
        }
    })
        .then(() => {
            activeNotification(
                'Notatka usunięta',
                'Pomyślnie usunięto notatkę',
                'success'
            );

            return [];
        })
        .catch(() => {
            activeNotification(
                'Coś poszło nie tak',
                'Niestety nie udało się usunąć notatki. Spróbuj ponownie.',
                'warning'
            );

            return [];
        });
};

const updateNote = (offerType: V2OfferType, note: string, id: string, noteId?: string): Promise<OfferData[]> => {
    return axiosApiInstance({
        method: 'put',
        url: 'note/process-uuid',
        params: {
            module: getPropertyByTypeUser(offerType)
        },
        data: {
            ownerId: id,
            content: note,
            noteId: !noteId ? undefined : noteId
        }
    })
        .then((resp) => {
            activeNotification(
                'Notatka zaaktualizowana',
                'Aktualizacja notatki przebiegła pomyślnie',
                'success'
            );

            return { ...resp.data, advertisementId: resp.data.advertisementId ? resp.data.advertisementId : id };
        })
        .catch(() => {
            activeNotification(
                'Coś poszło nie tak',
                'Niestety nie udało się zaaktualizować notatki. Spróbuj ponownie.',
                'warning'
            );
        });
};

const getOwnerProfile = (profileId: number, page: number, size: number): Promise<AxiosResponse> => {
    return axiosApiInstance({
        method: 'get',
        url: '/v2/user-offer/profile/advertisements',
        params: {
            profileId,
            page,
            size
        }
    })
        .then((resp) => {
            if (resp.data) {
                return {
                    ...resp.data,
                    content: resp.data.content.map((el: UserOffersResponseContent) => ({
                        ...el,
                        price: el.price ? convertPrice(el.price) : '',
                        maximumInvestmentValue: el.maximumInvestmentValue ? convertPrice(el.maximumInvestmentValue) : '',
                        area: el.area ? convertPrice(el.area) : '',
                        investorContribution: el.investorContribution ? convertPrice(el.investorContribution) : ''
                    }))
                };
            }
        })
        .catch(() => {
            activeNotification(
                'Nie znaleziono użytkownika',
                'Wyszukiwanie nie powiodło się, spróbuj jeszcze raz.',
                'warning'
            );

            return [{ empty: true }];
        });
};

const saveProfilePhoto = (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);

    return axiosApiInstance.post('/user/profile/image/create', formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    })
        .then((resp) => {
            if (resp.data) {
                return resp.data;
            }
        })
        .catch((err) => {
            if (err?.response?.status === 400) {
                activeNotification(
                    'Błąd',
                    err.response.data,
                    'warning'
                );

                return;
            }

            activeNotification(
                'Nie udało się zapisać zdjęcia',
                'Spróbuj ponownie',
                'warning'
            );
        });
};

const getProfile = (): Promise<AxiosResponse> => {
    return axiosApiInstance({
        method: 'get',
        url: 'user/profile/info'
    })
        .then((resp) => {
            if (resp.status === 204) {
                return {
                    name: '',
                    email: '',
                    phoneNumber: '',
                    description: '',
                    userPhoto: ''
                };
            }

            if (resp.data) {
                return resp.data;
            }
        })
        .catch(({ response }) => {
            activeNotification(
                'Nie udało się pobrać profilu',
                response.data,
                'warning'
            );

            return {
                name: '',
                email: '',
                phoneNumber: '',
                description: '',
                userPhoto: ''
            };
        });
};

const updateProfile = (params: MyDataDto, onDeletePhoto?: boolean): Promise<AxiosResponse> => {
    return axiosApiInstance({
        headers: { 'Content-Type': 'application/json' },
        method: 'put',
        url: '/user/profile/update',
        data: {
            profileName: params.profileName,
            profilePhone: params.profilePhone,
            profileEmail: params.profileEmail,
            description: params.description,
            userPhoto: params.userPhoto
        }
    })
        .then((resp) => {
            if (resp.data) {
                activeNotification(
                    onDeletePhoto ? 'Zdjęcie profilowe' : 'Edycja Twojego profilu',
                    onDeletePhoto ? 'Twoje zdjęcie profilowe zostało usunięte' : 'Twój profil został zaktualizowany pomyślnie',
                    'success'
                );

                return resp.data;
            }
        })
        .catch(({ response }) => {
            activeNotification(
                'Nie udało się aktualizować profilu',
                response.data,
                'warning'
            );
        });
};

const handleEditOffer = (params: UserAddAdvertStateType): Promise<AxiosResponse> => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { locations, ...paramsWithoutLocations } = params;
    const locationIds = Object.keys(params.savedLocations).map((id) => {
        return { id: Number(id) };
    });

    const requestParams = {
        ...paramsWithoutLocations,
        filterLocations: locationIds,
        price: stringToNumber(params.price),
        area: stringToNumber(params.area),
        expectedProfit: stringToNumber(params.expectedProfit),
        renovationExpenses: stringToNumber(params.renovationExpenses),
        investmentBudget: stringToNumber(params.investmentBudget),
        roi: stringToNumber(params.roi),
        leaseFee: stringToNumber(params.leaseFee),
        ownContribution: stringToNumber(params.ownContribution),
        investorContribution: stringToNumber(params.investorContribution),
        totalInvestmentValue: stringToNumber(params.totalInvestmentValue),
        minimumSalePrice: stringToNumber(params.minimumSalePrice),
        maximumSalePrice: stringToNumber(params.maximumSalePrice),
        formOfProfit: params.formOfProfit,
        minimumProfitAmount: stringToNumber(params.minimumProfitAmount),
        expectedReturnRate: stringToNumber(params.expectedReturnRate),
        floor: stringToNumber(params.floor),
        floors: stringToNumber(params.floors),
        rentalIncome: stringToNumber(params.rentalIncome),
        propertyCondition: params.propertyCondition,
        percentageOfInvestment: stringToNumber(params.percentageOfInvestment),
        investmentAmountFrom: stringToNumber(params.investmentAmountFrom),
        investmentAmountTo: stringToNumber(params.investmentAmountTo),
        minimumInvestmentValue: stringToNumber(params.minimumInvestmentValue),
        maximumInvestmentValue: stringToNumber(params.maximumInvestmentValue),
        investmentTimeFrom: stringToNumber(params.investmentTimeFrom),
        investmentTimeTo: stringToNumber(params.investmentTimeTo),
        availableFrom: params.availableFrom ? new Date(params.availableFrom) : undefined,
        estimatedSaleDate: params.estimatedSaleDate ? new Date(params.estimatedSaleDate) : undefined
    };

    return axiosApiInstance({
        headers: { 'Content-Type': 'application/json' },
        method: 'put',
        url: `/v2/user-offer/advertisement/edit/${params.offerType}/save`,
        data: requestParams
    })
        .then((resp) => {
            activeNotification(
                'Udało się!',
                'Ogłoszenie zostało zaktualizowane',
                'success'
            );

            return resp;
        })
        .catch((err) => {
            if (err?.response?.status === 400) {
                activeNotification(
                    'Błąd',
                    err.response.data,
                    'danger'
                );

                return Promise.reject(err);
            } else {
                activeNotification(
                    'Coś poszło nie tak!',
                    'Spróbuj ponownie',
                    'danger'
                );

                return Promise.reject(err);
            }
        });
};

const getMessages = (module: MessagesAdvertType): Promise<AxiosResponse> => {
    return axiosApiInstance({
        method: 'get',
        url: `/v2/user-offer/message/my/${module}`
    })
        .then((resp) => {
            if (resp.data) {
                return resp.data;
            }
        })
        .catch(() => {
            activeNotification(
                'Nie znaleziono wiadomości',
                'Wyszukiwanie nie powiodło się, spróbuj jeszcze raz.',
                'warning'
            );
        });
};

const sendMessage = (module: string, messageSender: MessageSenders, content: string, advertisementId: string, firstMessageId?: string): Promise<AxiosResponse> => {
    return axiosApiInstance({
        method: 'post',
        url: '/v2/user-offer/message/send',
        params: {
            module
        },
        data: {
            content,
            advertisementId,
            messageSender,
            firstMessageId
        }
    })
        .then((resp) => {
            if (resp.data) {
                return resp.data;
            }
        })
        .catch(() => {
            activeNotification(
                'Nie udało się wysłać wiadomości',
                'Wysyłanie wiadomości nie powiodło się, spróbuj jeszcze raz.',
                'warning'
            );
        });
};

const removeMessages = (lastMessageId: string): Promise<AxiosResponse> => {
    return axiosApiInstance({
        method: 'delete',
        url: '/v2/user-offer/message/delete',
        params: {
            lastMessageId
        }
    })
        .then((resp) => {
            if (resp.data) {
                return resp.data;
            }
        })
        .catch(() => {
            activeNotification(
                'Nie udało się wysłać wiadomości',
                'Wysyłanie wiadomości nie powiodło się, spróbuj jeszcze raz.',
                'warning'
            );
        });
};

const getMessagesDetail = (lastMessageId: string): Promise<AxiosResponse> => {
    return axiosApiInstance({
        method: 'get',
        url: '/v2/user-offer/message/detail',
        params: {
            lastMessageId
        }
    })
        .then((resp) => {
            if (resp.data) {
                return resp.data;
            }
        })
        .catch(() => {
            activeNotification(
                'Nie znaleziono wiadomości',
                'Wyszukiwanie nie powiodło się, spróbuj jeszcze raz.',
                'warning'
            );
        });
};

const readMessage = (lastMessageId: string): Promise<AxiosResponse> => {
    return axiosApiInstance({
        method: 'put',
        url: '/v2/user-offer/message/read',
        params: {
            lastMessageId
        }
    })
        .then((resp) => {
            if (resp.data) {
                return resp.data;
            }
        })
        .catch(() => {
            activeNotification(
                'Nie znaleziono wiadomości',
                'Wyszukiwanie nie powiodło się, spróbuj jeszcze raz.',
                'warning'
            );
        });
};

const checkIfProfileExists = async () => {
    return axiosApiInstance.get('/user/profile/activation-status')
        .then(() => {
            return true;
        })
        .catch(() => {
            return false;
        });
};

const changeVisibilityOffer = async (module: string, advertisementId: string, value: boolean): Promise<AxiosResponse> => {
    return axiosApiInstance({
        method: 'put',
        url: '/v2/user-offer/advertisement/edit/status',
        params: {
            module,
            advertisementId,
            status: value
        }
    })
        .then((resp) => {
            if (resp.data) {
                activeNotification(
                    'Edycja ogłoszenia',
                    'Aktualizacja widoczności przebiegła pomyślnie.',
                    'success'
                );

                return resp.data;
            }
        })
        .catch(() => {
            activeNotification(
                'Edycja ogłoszenia',
                'Aktualizacja zmiany widoczności oferty nie powiodła się, spróbuj jeszcze raz.',
                'warning'
            );
        });
};

const changePushNotificationStatus = async (value: boolean): Promise<AxiosResponse> => {
    return axiosApiInstance({
        method: 'put',
        url: `user/notification/edit/push-notification?activeStatus=${value}`
    })
        .then((resp) => {
            if (resp.data) {
                activeNotification(
                    'Udało się!',
                    'Aktualizacja ustawień notyfikacji przebiegła pomyślnie.',
                    'success'
                );

                return resp.data;
            }
        })
        .catch(() => {
            activeNotification(
                'Ups, coś poszło nie tak',
                'Aktualizacja ustawień notyfikacji nie powiodła się, spróbuj jeszcze raz.',
                'warning'
            );
        });
};

const changeNotificationMailAndPhoneNumber = async (email: string, phone: string): Promise<AxiosResponse> => {
    return axiosApiInstance({
        method: 'put',
        url: 'user/notification/edit/notification',
        data: {
            notificationEmail: email,
            notificationPhone: phone
        }
    })
        .then((resp) => {
            if (resp.data) {
                activeNotification(
                    'Udało się!',
                    'Edyja danych do wysyłania notyfikacji przebiegła pomyślnie.',
                    'success'
                );

                return resp.data;
            }
        })
        .catch(() => {
            activeNotification(
                'Ups, coś poszło nie tak',
                'Edyja danych do wysyłania notyfikacji nie powiodła się, spróbuj jeszcze raz.',
                'warning'
            );
        });
};

const changeSmsAndMailNotificationStatus = async (value: boolean): Promise<AxiosResponse> => {
    return axiosApiInstance({
        method: 'put',
        url: `user/notification/edit/active-notification?activeStatus=${value}`
    })
        .then((resp) => {
            if (resp.data) {
                activeNotification(
                    'Udało się!',
                    'Aktualizacja ustawień powiadomień SMS/mail przebiegła pomyślnie.',
                    'success'
                );

                return resp.data;
            }
        })
        .catch(() => {
            activeNotification(
                'Ups, coś poszło nie tak',
                'Aktualizacja ustawień powiadomień SMS/mail nie powiodła się, spróbuj jeszcze raz.',
                'warning'
            );
        });
};

const changeDayLimit = async (value: number): Promise<AxiosResponse> => {
    return axiosApiInstance({
        method: 'put',
        url: `user/notification/edit/day-limit?dayLimit=${value}`
    })
        .then((resp) => {
            if (resp.data) {
                activeNotification(
                    'Udało się!',
                    'Aktualizacja dziennego limitu SMS przebiegła pomyślnie.',
                    'success'
                );

                return resp.data;
            }
        })
        .catch(() => {
            activeNotification(
                'Ups, coś poszło nie tak',
                'Aktualizacja dziennego limitu SMS nie powiodła się, spróbuj jeszcze raz.',
                'warning'
            );
        });
};

const getActivePackageInfo = (): Promise<AxiosResponse> => {
    return axiosApiInstance({
        method: 'get',
        url: 'user/active-package/info'
    })
        .then((resp) => {
            if (resp.data) {
                return resp.data;
            }
        })
        .catch(({ response }) => {
            activeNotification(
                'Nie udało się  informacji o pakiecie',
                response.data,
                'warning'
            );
        });
};

const getNotificationSettings = (): Promise<AxiosResponse> => {
    return axiosApiInstance({
        method: 'get',
        url: 'user/notification/info'
    })
        .then((resp) => {
            if (resp.status === 204) {
                return {
                    name: '',
                    email: '',
                    phoneNumber: '',
                    description: '',
                    userPhoto: ''
                };
            }

            if (resp.data) {
                return resp.data;
            }
        })
        .catch(({ response }) => {
            activeNotification(
                'Nie udało się pobrać profilu',
                response.data,
                'warning'
            );

            return {
                name: '',
                email: '',
                phoneNumber: '',
                description: '',
                userPhoto: ''
            };
        });
};

const getTokenInfo = (): Promise<AxiosResponse> => {
    return axiosApiInstance({
        method: 'get',
        url: 'user/ai-token/info'
    })
        .then((resp) => {
            if (resp.status === 204) {
                return {
                    monthAiTokensToUse: '',
                    boughtAiToken: 0,
                    aiTokenReset: ''
                };
            }

            if (resp.data) {
                return resp.data;
            }
        })
        .catch(() => {
            return {
                monthAiTokensToUse: '',
                boughtAiToken: 0,
                aiTokenReset: ''
            };
        });
};

const getUserResourceInfo = (): Promise<UserResourcesDto> => {
    return axiosApiInstance({
        method: 'get',
        url: '/user/resources/info'
    })
        .then((resp) => {
            if (resp.data) {
                return resp.data;
            }
        })
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId);
        });
};

const sendPushNotificationToken = (token: string): Promise<AxiosResponse> => {
    return axiosApiInstance({
        method: 'put',
        url: `/user/notification/edit/push-notification/token?appToken=${token}&appType=WEB`
    })
        .then((resp) => {
            if (resp.data) {
                return resp.data;
            }
        })
        .catch(() => {
            activeNotification(
                'Nie udało się wysłać tokena',
                'Wysyłanie tokena nie powiodło się, spróbuj jeszcze raz.',
                'warning'
            );
        });
};

const logException = (error: Error, id: string, context?: LogExceptionContext) => {
    return axiosApiInstance({
        method: 'post',
        url: '/user/log-error',
        data: {
            data: JSON.stringify({
                errorMessage: error.message,
                errorStack: error.stack,
                errorId: id,
                context: {
                    ...context,
                    userAgent: context?.userAgent || (typeof window !== 'undefined' ? window.navigator.userAgent : undefined),
                    timestamp: context?.timestamp || new Date().toISOString(),
                    location: context?.location || (typeof window !== 'undefined' ? window.location.href : undefined)
                }
            })
        }
    });
};

const checkOpinion = async (opinionType: CheckOpinionParamsOpinionTypeEnum): Promise<boolean> => {
    return axiosApiInstance.get('/user/opinion/check', {
        params: {
            opinionType
        }
    })
        .then(() => {
            return true;
        })
        .catch((error) => {
            if (error?.response?.status === 404) { // 404 oznacz brak opinii
                return false;
            }

            return false;
        });
};

const sendOpinion = async (opinionData: { opinionType: CheckOpinionParamsOpinionTypeEnum, opinionText: string, rating: number }) => {
    return axiosApiInstance.post('/user/opinion/send', opinionData)
        .then((resp) => {
            activeNotification(
                'Dziękujemy!',
                'Twoja opinia została wysłana',
                'success'
            );

            return resp.data;
        })
        .catch((error) => {
            if (error?.response?.status === 400) {
                activeNotification(
                    'Błąd',
                    error.response.data,
                    'danger'
                );
            } else {
                activeNotification(
                    'Coś poszło nie tak!',
                    'Nie udało się wysłać opinii. Spróbuj ponownie.',
                    'danger'
                );
            }

            return Promise.reject(error);
        });
};

const createAccount = async (email: string, password: string, confirmPassword: string, firstName: string, lastName: string, companyName: string, phoneNumber: string) => {
    return axiosApiInstance.post('/user/account/create', {
        email,
        password,
        confirmPassword,
        firstName,
        lastName,
        phoneNumber,
        companyName
    })
        .then((resp) => {
            if (resp.status === 201) {
                activeNotification(
                    'Sukces!',
                    'Konto zostało pomyślnie utworzone',
                    'success'
                );

                return true;
            }

            return false;
        })
        .catch(({ response }) => {
            switch (response?.status) {
            case 400:
                activeNotification(
                    'Błędne dane',
                    response.data?.message || 'Sprawdź poprawność wprowadzonych danych',
                    'danger'
                );
                break;
            case 423:
                activeNotification(
                    'Błędne dane',
                    response.data || 'Sprawdź poprawność wprowadzonych danych',
                    'danger'
                );
                break;
            case 500:
                activeNotification(
                    'Błąd serwera',
                    'Spróbuj ponownie później',
                    'danger'
                );
                break;
            default:
                activeNotification(
                    'Błąd',
                    'Nie udało się utworzyć konta',
                    'danger'
                );
                break;
            }

            return false;
        });
};

const deleteAccount = async () => {
    return axiosApiInstance.delete('/user/account/delete')
        .then(() => {
            activeNotification(
                'Konto usunięte',
                'Twoje konto zostało trwale usunięte',
                'success'
            );

            return true;
        })
        .catch(({ response }) => {
            switch (response?.status) {
            case 400:
                activeNotification(
                    'Błąd',
                    response.data?.message || 'Nie można usunąć konta',
                    'danger'
                );
                break;
            case 500:
                activeNotification(
                    'Błąd serwera',
                    'Spróbuj ponownie później',
                    'danger'
                );
                break;
            default:
                activeNotification(
                    'Błąd',
                    'Nie udało się usunąć konta',
                    'danger'
                );
                break;
            }

            return false;
        });
};

const changePassword = (data: { oldPassword: string, password: string, confirmPassword: string }) => {
    return axiosApiInstance
        .patch('/user/account/change-password', data)
        .then(() => {
            activeNotification(
                'Zmiana hasła',
                'Hasło zostało zmienione',
                'success'
            );

            return true;
        })
        .catch(({ response }) => {
            switch (response?.status) {
            case 400:
                activeNotification(
                    'Błąd',
                    response.data || 'Nie udało się zmienić hasła',
                    'danger'
                );
                break;
            case 409:
                activeNotification(
                    'Błąd',
                    response.data || 'Nie udało się zmienić hasła',
                    'danger'
                );
                break;
            case 500:
                activeNotification(
                    'Błąd serwera',
                    'Spróbuj ponownie później',
                    'danger'
                );
                break;
            default:
                activeNotification(
                    'Błąd',
                    'Nie udało się zmienić hasła',
                    'danger'
                );
                break;
            }

            return false;
        });
};

const resetPasswordWithLink = (data: { password: string, confirmPassword: string, token: string }) => {
    return axiosApiInstance
        .patch('/user/account/link/reset-password', data)
        .then(() => {
            activeNotification('Sukces', 'Hasło zostało zresetowane. Możesz się zalogować.', 'success');

            return true;
        })
        .catch(({ response }) => {
            switch (response?.status) {
            case 400:
                activeNotification(
                    'Błąd',
                    response.data || 'Nie udało się zmienić hasła',
                    'danger'
                );
                break;
            case 409:
                activeNotification(
                    'Błąd',
                    response.data || 'Nie udało się zmienić hasła',
                    'danger'
                );
                break;
            case 410:
                activeNotification(
                    'Błąd',
                    response.data || 'Nie udało się zmienić hasła',
                    'danger'
                );
                break;
            case 500:
                activeNotification(
                    'Błąd serwera',
                    'Spróbuj ponownie później',
                    'danger'
                );
                break;
            default:
                activeNotification(
                    'Błąd',
                    'Nie udało się zmienić hasła',
                    'danger'
                );
                break;
            }

            return false;
        });
};

const restorePasswordByEmail = (data: { email: string }) => {
    return axiosApiInstance
        .post('/user/account/link/restore-password', data)
        .then(() => {
            activeNotification(
                'Sukces',
                'Na podany email został wysłany link do zresetowania hasła',
                'success'
            );

            return true;
        })
        .catch(({ response }) => {
            switch (response?.status) {
            case 400:
                activeNotification(
                    'Błąd',
                    response.data || 'Niepoprawny adres email',
                    'danger'
                );
                break;
            case 500:
                activeNotification(
                    'Błąd serwera',
                    'Spróbuj ponownie później',
                    'danger'
                );
                break;
            default:
                activeNotification(
                    'Błąd',
                    'Nie udało się zmienić hasła',
                    'danger'
                );
                break;
            }

            return false;
        });
};

export const userApiRequests = {
    getAllOffers,
    authenticate,
    authenticateByToken,
    logout,
    getSavedFilters,
    getFilter,
    updateFilter,
    addFilter,
    deleteFilter,
    getOffers,
    getMyOffers,
    getMyOfferDetails,
    getOfferDetails,
    addNewOffer,
    setOfferReaction,
    getNotes,
    deleteNote,
    updateNote,
    addImage,
    getOwnerProfile,
    deleteMyOffer,
    handleEditOffer,
    getProfile,
    saveProfilePhoto,
    updateProfile,
    getMessages,
    sendMessage,
    getMessagesDetail,
    removeMessages,
    readMessage,
    addFile,
    checkIfProfileExists,
    changeVisibilityOffer,
    downloadFile,
    changePushNotificationStatus,
    changeSmsAndMailNotificationStatus,
    changeNotificationMailAndPhoneNumber,
    changeDayLimit,
    getActivePackageInfo,
    getNotificationSettings,
    sendPushNotificationToken,
    logException,
    checkOpinion,
    sendOpinion,
    getTokenInfo,
    getUserResourceInfo,
    createAccount,
    deleteAccount,
    changePassword,
    resetPasswordWithLink,
    restorePasswordByEmail
};
